import { Alert } from '@ps/isp-web-component-library';
import { Translation } from '@ps/web-shared-components';
import React, { useEffect, useRef, useState } from 'react';
import { useField } from 'react-final-form';

import { PROMOTION_ALERT_DISMISS_DELAY } from '@swf/sports/utils/constants';

import {
  TXTWCL_Bonus_Cash_Removed,
  TXTWCL_Bonus_Cash_stake_amount_applie_ELL,
  TXTWCL_Free_Bet_removed,
  TXTWCL_Free_Bet_stake_amount_applied__ELL
} from './PromotionAlert.messages.json';

const promotionAlertTranslationMap = {
  bonusCashApplied: TXTWCL_Bonus_Cash_stake_amount_applie_ELL,
  bonusCashRemoved: TXTWCL_Bonus_Cash_Removed,
  freeBetApplied: TXTWCL_Free_Bet_stake_amount_applied__ELL,
  freeBetRemoved: TXTWCL_Free_Bet_removed
};

const PromotionAlert: React.FC<{ fieldName: string }> = ({ fieldName }) => {
  const [isVisible, setIsVisible] = useState(false);

  const { input: bonusCashInput } = useField(`${fieldName}.bonusCash`, {
    subscription: { value: true }
  });

  const { input: freeBetInput } = useField(`${fieldName}.freeBet`, {
    subscription: { value: true }
  });

  const {
    meta: { error: stakeError }
  } = useField(`${fieldName}.stakePerLine`, {
    subscription: {
      error: true
    }
  });

  const isBonusCashChecked = Boolean(bonusCashInput.value?.length);
  const isFreeBetChecked = Boolean(freeBetInput.value?.length);

  const previousBonusCashCheckedState = useRef(false);
  const previousFreeBetCheckedState = useRef(false);

  const isAppliedFreeBet = isFreeBetChecked;
  const isRemovedFreeBet =
    !isFreeBetChecked && previousFreeBetCheckedState.current && !isBonusCashChecked;
  const isAppliedBonusCash = isBonusCashChecked;
  const isRemovedBonusCash = !isBonusCashChecked && previousBonusCashCheckedState.current;

  let currentPromotionAlert: keyof typeof promotionAlertTranslationMap | undefined;

  if (isAppliedFreeBet) {
    currentPromotionAlert = 'freeBetApplied';
  } else if (isRemovedFreeBet) {
    currentPromotionAlert = 'freeBetRemoved';
  } else if (isAppliedBonusCash) {
    currentPromotionAlert = 'bonusCashApplied';
  } else if (isRemovedBonusCash) {
    currentPromotionAlert = 'bonusCashRemoved';
  }

  const timeoutRef = useRef<NodeJS.Timeout>(undefined);

  const applyTimeout = () => {
    clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => setIsVisible(false), PROMOTION_ALERT_DISMISS_DELAY);
  };

  useEffect(() => {
    if (currentPromotionAlert) {
      setIsVisible(true);

      if (isAppliedFreeBet || isRemovedFreeBet) {
        previousFreeBetCheckedState.current = true;
        previousBonusCashCheckedState.current = false;
      } else if (isAppliedBonusCash || isRemovedBonusCash) {
        previousBonusCashCheckedState.current = true;
        previousFreeBetCheckedState.current = false;
      }

      applyTimeout();

      return () => {
        clearTimeout(timeoutRef.current);
      };
    }
  }, [
    currentPromotionAlert,
    isAppliedBonusCash,
    isAppliedFreeBet,
    isRemovedBonusCash,
    isRemovedFreeBet
  ]);

  if (!isVisible || stakeError || !currentPromotionAlert) return null;

  return (
    <Alert level="promotion" variant="passive-aggressive">
      <Translation {...promotionAlertTranslationMap[currentPromotionAlert]} />
    </Alert>
  );
};

export default PromotionAlert;
